import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, tokens, activeTenant } from "./api";
import type { Capability, LoginResponse, TenantMini, User } from "../types/api";

export const ALL_TENANTS = "__all__";

interface AuthState {
  user: User | null;
  loading: boolean;
  tenant: TenantMini | null; // null when the all-tenants view is active
  tenants: TenantMini[];
  activeSlug: string | null; // raw selection (may be ALL_TENANTS)
  allTenants: boolean; // super-admin cross-tenant view active
  capabilities: Capability[];
  can: (cap: Capability) => boolean;
  login: (username: string, password: string) => Promise<User>;
  logout: () => void;
  selectTenant: (slug: string) => void;
}

const Ctx = createContext<AuthState>(null as never);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState<string | null>(activeTenant.get());

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!tokens.access) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get<User>("/auth/me/");
        if (cancelled) return;
        setUser(data);
        if (!tenantSlug) {
          const def = data.home_tenant?.slug ?? data.tenants[0]?.slug;
          if (def) {
            activeTenant.set(def);
            setTenantSlug(def);
          }
        }
      } catch {
        tokens.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(username: string, password: string) {
    const { data } = await api.post<LoginResponse>("/auth/login/", { username, password });
    tokens.set(data.access, data.refresh);
    setUser(data.user);
    const def = data.user.home_tenant?.slug ?? data.user.tenants[0]?.slug;
    if (def) {
      activeTenant.set(def);
      setTenantSlug(def);
    }
    return data.user;
  }

  function logout() {
    tokens.clear();
    setUser(null);
  }

  function selectTenant(slug: string) {
    activeTenant.set(slug);
    setTenantSlug(slug);
  }

  const allTenants = !!user?.is_platform_admin && tenantSlug === ALL_TENANTS;

  const tenant = useMemo(() => {
    if (tenantSlug === ALL_TENANTS) return null;
    return user?.tenants.find((t) => t.slug === tenantSlug) ?? user?.home_tenant ?? user?.tenants[0] ?? null;
  }, [user, tenantSlug]);

  const capabilities = useMemo<Capability[]>(() => {
    if (!user) return [];
    if (allTenants) {
      // Union across all tenants (a platform admin holds the full set).
      const set = new Set<Capability>();
      Object.values(user.capabilities_by_tenant ?? {}).forEach((caps) => caps.forEach((c) => set.add(c)));
      return [...set];
    }
    return tenant ? user.capabilities_by_tenant?.[tenant.slug] ?? [] : [];
  }, [user, tenant, allTenants]);

  const value: AuthState = {
    user,
    loading,
    tenant,
    tenants: user?.tenants ?? [],
    activeSlug: tenantSlug,
    allTenants,
    capabilities,
    can: (cap) => capabilities.includes(cap),
    login,
    logout,
    selectTenant,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
